import { Mark, Node, type AnyExtension, type Editor } from '@tiptap/core';
import type { Mark as ProseMirrorMark, Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

// The markers pkg/service/api/emailtemplate writes into a draft made from a template.
export const FILLED_FIELD_ATTRIBUTE = 'data-email-field';
export const MISSING_FIELD_ATTRIBUTE = 'data-email-field-missing';
export const CARA_SECTION_ATTRIBUTE = 'data-cara-section';

// Above the editor's TextStyle and div rules, which would otherwise take these elements.
export const MARKER_PARSE_PRIORITY = 60;

const markerAttribute = (name: string, htmlAttribute: string) => ({
  default: null,
  parseHTML: (element: HTMLElement) => element.getAttribute(htmlAttribute),
  renderHTML: (attributes: Record<string, string | null>) => {
    const value = attributes[name];
    return value ? { [htmlAttribute]: value } : {};
  }
});

type FieldRun = { from: number; to: number; text: string; mark: ProseMirrorMark };

/** Each stretch of text carrying one field mark, joined across bold or italic splits. */
const fieldRuns = (doc: ProseMirrorNode, fieldMarkName: string): FieldRun[] => {
  const runs: FieldRun[] = [];
  let open: FieldRun | null = null;
  doc.descendants((node, pos) => {
    if (!node.isText) {
      open = null;
      return true;
    }
    const mark = node.marks.find((candidate) => candidate.type.name === fieldMarkName);
    if (!mark) {
      open = null;
      return false;
    }
    if (open && open.to === pos && open.mark.eq(mark)) {
      open.to = pos + node.nodeSize;
      open.text += node.text ?? '';
    } else {
      open = { from: pos, to: pos + node.nodeSize, text: node.text ?? '', mark };
      runs.push(open);
    }
    return false;
  });
  return runs;
};

const editedFieldsKey = new PluginKey('emailDraftEditedFields');
const filledFieldLabelsKey = new PluginKey<boolean>('emailDraftFilledFieldLabels');

/**
 * Shows or hides each filled field's label, as `[[label]]` on hover. A decoration rather than an
 * attribute the mark renders, so the label never reaches the saved email.
 */
export const showFilledFieldLabels = (editor: Editor, isShowing: boolean) => {
  editor.view.dispatch(editor.state.tr.setMeta(filledFieldLabelsKey, isShowing));
};

/**
 * A field from the template: filled with Cara's value, or a blank still reading `[[label]]`. The
 * text it arrived with is kept beside it, so an edit that changes the text drops the mark, and
 * with it the highlight, rather than calling the agent's words Cara's.
 */
export const EmailFieldMark = Mark.create({
  name: 'emailField',
  // Typing at either edge of a field is the agent's own text.
  inclusive: false,

  addAttributes() {
    return {
      filled: markerAttribute('filled', FILLED_FIELD_ATTRIBUTE),
      missing: markerAttribute('missing', MISSING_FIELD_ATTRIBUTE),
      arrivedText: {
        default: '',
        parseHTML: (element: HTMLElement) => element.textContent ?? '',
        renderHTML: () => ({})
      }
    };
  },

  parseHTML() {
    return [
      { tag: `span[${FILLED_FIELD_ATTRIBUTE}]`, priority: MARKER_PARSE_PRIORITY },
      { tag: `span[${MISSING_FIELD_ATTRIBUTE}]`, priority: MARKER_PARSE_PRIORITY }
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', HTMLAttributes, 0];
  },

  addProseMirrorPlugins() {
    const fieldMarkName = this.name;
    return [
      new Plugin({
        key: editedFieldsKey,
        appendTransaction: (transactions, _, state) => {
          if (!transactions.some((transaction) => transaction.docChanged)) return null;
          const tr: Transaction = state.tr;
          for (const run of fieldRuns(state.doc, fieldMarkName)) {
            if (run.text !== run.mark.attrs.arrivedText) tr.removeMark(run.from, run.to, run.mark);
          }
          return tr.docChanged ? tr : null;
        }
      }),
      new Plugin<boolean>({
        key: filledFieldLabelsKey,
        state: {
          init: () => false,
          apply: (tr, isShowing) => tr.getMeta(filledFieldLabelsKey) ?? isShowing
        },
        props: {
          decorations: (state) => {
            if (!filledFieldLabelsKey.getState(state)) return null;
            const labels = fieldRuns(state.doc, fieldMarkName)
              .filter((run) => run.mark.attrs.filled)
              .map((run) =>
                Decoration.inline(run.from, run.to, { title: `[[${run.mark.attrs.filled}]]` })
              );
            return DecorationSet.create(state.doc, labels);
          }
        }
      })
    ];
  }
});

/** A part of the draft Cara wrote for one of the template's Cara sections. */
export const CaraWrittenSectionNode = Node.create({
  name: 'caraWrittenSection',
  group: 'block',
  content: 'block+',

  addAttributes() {
    return { sectionId: markerAttribute('sectionId', CARA_SECTION_ATTRIBUTE) };
  },

  parseHTML() {
    return [{ tag: `div[${CARA_SECTION_ATTRIBUTE}]`, priority: MARKER_PARSE_PRIORITY }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', HTMLAttributes, 0];
  }
});

/** What the composer's editor adds for an email drafted from a template. */
export const emailDraftMarkerExtensions: AnyExtension[] = [EmailFieldMark, CaraWrittenSectionNode];
